// src/services/questdb.js
import axios from 'axios';
import { API_URL as BASE_API_URL } from '../config.js';

const API_URL = `${BASE_API_URL}/questdb/query`;

class QuestDBService {
  // Execute custom SQL query (retries on 503 — agent may still be starting)
  async query(sql, plantId, _retries = 3) {
    try {
      const response = await axios.get(API_URL, {
        params: {
          sql,
          plantId: plantId || 'plantA'
        }
      });
      return this.formatData(response.data);
    } catch (error) {
      if (error.response?.status === 503 && _retries > 0) {
        await new Promise(r => setTimeout(r, 2000));
        return this.query(sql, plantId, _retries - 1);
      }
      console.error('Error executing query:', error);
      throw error;
    }
  }

  // Get OPC tag data (using actual table name: opc_tag_data)
  async getOpcTags(limit = 100, timeRange = '1') {
    const sql = `
      SELECT timestamp, tag_name, value, quality
      FROM opc_tag_data
      ORDER BY timestamp DESC
      LIMIT 200;
    `;
    return await this.query(sql);
  }

  // Get OPCUA tag data
  async getOpcuaTags(limit = 100, timeRange = '1') {
    try {
      const sql = `SELECT timestamp, tag_name, value, quality, staleness_ms, device_id 
                   FROM opcua_tag_data 
                   WHERE timestamp > dateadd('h', -${parseInt(timeRange)}, now()) 
                   ORDER BY timestamp DESC 
                   LIMIT ${limit}`;
      return await this.query(sql);
    } catch (error) {
      console.error('Error fetching OPCUA tags:', error);
      throw error;
    }
  }

  // Get OPC cycle stats
  async getOpcStats(limit = 100, timeRange = '1') {
    try {
      const sql = `SELECT timestamp, bridge_id, device_id, cycle, 
                          total_tags, success_count, error_count, avg_latency_ms
                   FROM opc_cycle_stats 
                   WHERE timestamp > dateadd('h', -${parseInt(timeRange)}, now()) 
                   ORDER BY timestamp DESC 
                   LIMIT ${limit}`;
      return await this.query(sql);
    } catch (error) {
      console.error('Error fetching OPC stats:', error);
      throw error;
    }
  }

  // Get sensor data (using actual table name: sensor_data)
  async getSensors(limit = 100, timeRange = '1') {
    try {
      const sql = `SELECT timestamp, device_id, mqtt_topic, bridge_id, 
                          source_broker, payload_size_bytes, data
                   FROM sensor_data 
                   WHERE timestamp > dateadd('h', -${parseInt(timeRange)}, now()) 
                   ORDER BY timestamp DESC 
                   LIMIT ${limit}`;
      return await this.query(sql);
    } catch (error) {
      console.error('Error fetching sensor data:', error);
      throw error;
    }
  }

  // Get available tables
  async getTables(plantId) {
    try {
      const sql = `SELECT table_name FROM tables()`;
      return await this.query(sql, plantId);
    } catch (error) {
      console.error('Error fetching tables:', error);
      throw error;
    }
  }

  // Get latest values by tag name
  async getLatestValues(tagNames = []) {
    try {
      const tagFilter = tagNames.length > 0 
        ? `WHERE tag_name IN (${tagNames.map(t => `'${t}'`).join(',')})`
        : '';
      
      const sql = `SELECT tag_name, value, timestamp, quality, device_id
                   FROM opc_tag_data 
                   ${tagFilter}
                   LATEST ON timestamp PARTITION BY tag_name`;
      return await this.query(sql);
    } catch (error) {
      console.error('Error fetching latest values:', error);
      throw error;
    }
  }

  // Get aggregated tag data
  async getTagAggregates(tagName, timeRange = '1') {
    try {
      const sql = `SELECT 
                     timestamp,
                     tag_name,
                     avg(value) as avg_value,
                     min(value) as min_value,
                     max(value) as max_value,
                     count() as count
                   FROM opc_tag_data 
                   WHERE tag_name = '${tagName}'
                     AND timestamp > dateadd('h', -${parseInt(timeRange)}, now())
                   SAMPLE BY 5m ALIGN TO CALENDAR
                   ORDER BY timestamp DESC`;
      return await this.query(sql);
    } catch (error) {
      console.error('Error fetching tag aggregates:', error);
      throw error;
    }
  }

  // Format QuestDB response to array of objects
  // ✅ UPDATED: Now handles both array and object formats
  formatData(questdbResponse) {
  if (!questdbResponse || !questdbResponse.dataset || !questdbResponse.columns) {
    return [];
  }

  const columns = questdbResponse.columns;
  const dataset = questdbResponse.dataset;

  if (dataset.length === 0) {
    return [];
  }

  const firstRow = dataset[0];
  const isArrayFormat = Array.isArray(firstRow);
  
  // Numeric types that need coercion
  const numericTypes = ['DOUBLE', 'REAL', 'INTEGER', 'BIGINT', 'SMALLINT', 'FLOAT', 'NUMERIC', 'DECIMAL'];

  return dataset.map(row => {
    const obj = {};
    
    columns.forEach((col, index) => {
      let value = isArrayFormat ? row[index] : row[col.name];
      
      // Handle timestamps
      if (col.type === 'TIMESTAMP') {
        value = this.formatTimestamp(value);
      }
      
      // ✅ Coerce numeric types to actual numbers
      if (numericTypes.includes(col.type?.toUpperCase())) {
        if (typeof value === 'string' && value.trim() !== '') {
          const num = Number(value);
          value = isNaN(num) ? 0 : num;
        } else if (value === null || value === undefined || value === '') {
          value = 0;
        }
      }
      
      obj[col.name] = value;
    });
    
    return obj;
  });
}

  // Format timestamp for display
  formatTimestamp(timestamp) {
    if (!timestamp) return null;

    let date;
    
    // QuestDB returns timestamps as microseconds
    if (typeof timestamp === 'number') {
      if (timestamp > 1000000000000000) {
        // Microseconds
        date = new Date(timestamp / 1000);
      } else if (timestamp > 1000000000000) {
        // Milliseconds
        date = new Date(timestamp);
      } else {
        // Seconds
        date = new Date(timestamp * 1000);
      }
    } else {
      date = new Date(timestamp);
    }

    if (isNaN(date.getTime())) {
      return timestamp;
    }

    return date.toISOString();
  }

  // Parse a DB timestamp value as UTC. DBs commonly return strings like
  // "2026-06-02 12:34:56" or "2026-06-02T12:34:56" with no timezone suffix.
  // `new Date(str)` interprets these as LOCAL time on most browsers (a
  // historical JS misfeature). We force a UTC reading so the timezone
  // conversion below is correct.
  parseAsUtc(raw) {
    if (raw == null) return null;
    if (raw instanceof Date) return raw;
    if (typeof raw === 'number') {
      // microseconds / ms / seconds heuristic (matches formatTimestamp)
      if (raw > 1e15) return new Date(raw / 1000);
      if (raw > 1e12) return new Date(raw);
      return new Date(raw * 1000);
    }
    let s = String(raw).trim();
    // Already has timezone? Use as-is.
    if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
    // MySQL "YYYY-MM-DD HH:MM:SS[.fff]" → ISO with Z
    s = s.replace(' ', 'T') + 'Z';
    const d = new Date(s);
    return isNaN(d.getTime()) ? new Date(raw) : d;
  }

  // Format for charts. xAxisMode='timestamp' (default) parses the field as a
  // UTC Date and produces an HH:mm:ss label in the requested timezone.
  // xAxisMode='simple' uses the raw value as the X-axis label without date
  // parsing (e.g. machine_id, region, batch_no).
  //
  // Signature: (data, timestampField, timezone, xAxisMode, valueField).
  // The `timezone` slot used to be `valueField` — kept ordering for caller
  // compatibility (panels pass timezone as the 3rd arg today).
  formatForChart(data, timestampField = 'timestamp', timezone = 'UTC', xAxisMode = 'timestamp', valueField = 'value', xAxisFields = null) {
    // Simple-mode composite labels: when xAxisFields is a non-empty array,
    // join those columns with " — " for the X-axis tick. Falls back to single
    // timestampField for backwards compat.
    const fieldsForSimple = Array.isArray(xAxisFields) && xAxisFields.length
      ? xAxisFields
      : [timestampField];

    const rows = data.map(item => {
      const raw = item[timestampField];

      if (xAxisMode === 'simple') {
        const label = fieldsForSimple
          .map(f => {
            const v = item[f];
            return v == null ? '' : String(v);
          })
          .filter(Boolean)
          .join(' — ');
        return {
          ...item,
          _time: label,
          _timestamp: raw,
          _value: item[valueField]
        };
      }

      let displayTime = 'N/A';
      const date = this.parseAsUtc(raw);
      if (date && !isNaN(date.getTime())) {
        try {
          displayTime = date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: timezone || 'UTC'
          });
        } catch {
          // Invalid IANA zone — fall back to UTC
          displayTime = date.toLocaleTimeString('en-GB', {
            hour: '2-digit', minute: '2-digit', second: '2-digit',
            hour12: false, timeZone: 'UTC'
          });
        }
      }
      return {
        ...item,
        _time: displayTime,
        _timestamp: raw,
        _value: item[valueField]
      };
    });

    // Both modes: query is ORDER BY ... DESC LIMIT N (so we get the latest
    // N rows). Reverse so the chart renders left→right in ascending order.
    return rows.reverse();
  }
}

export default new QuestDBService();
